import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import designerService, { Designer } from '../services/designer';
import AuthenticatedImage from '../components/AuthenticatedImage';

const Designers: React.FC = () => {
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDesigners = async () => {
      try {
        setLoading(true);
        setError(null);
        const designersData = await designerService.getDesigners();
        // Filter only active designers
        setDesigners(designersData.filter(designer => designer.isActive));
      } catch (error) {
        console.error('Error loading designers:', error);
        setError('Failed to load designers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadDesigners();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 py-4">
          <h1 className="text-2xl font-serif text-gray-900">Designers</h1>
          <p className="text-gray-600 mt-1">Discover our exclusive authorised designers.</p>
        </div>
      </header>

      {/* Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-gray-200"></div>
                <div className="p-4">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : designers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No designers available at the moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {designers.map((designer) => (
              <Link
                key={designer.id}
                to={`/home/designers/${designer.id}`}
                className="block rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="aspect-[2/1] relative">
                  {/* Banner Background */}
                  {designer.bannerUrl ? (
                    <AuthenticatedImage
                      src={designer.bannerUrl}
                      alt={`${designer.name} banner`}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gray-200" />
                  )}

                  {/* Overlay for better logo visibility */}
                  <div className="absolute inset-0 bg-black/20" />

                  {/* Logo Centered */}
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    {designer.logoUrl ? (
                      <AuthenticatedImage
                        src={designer.logoUrl}
                        alt={designer.name}
                        className="object-contain max-w-[70%] max-h-[70%]"
                      />
                    ) : (
                      <h3 className="text-xl font-serif text-white text-center drop-shadow-lg">
                        {designer.name}
                      </h3>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Designers;